import classNames from "classnames"
import { Swiper, SwiperSlide } from "swiper/react"

import Link from "@components/elements/Link"
import PillButton from "@components/elements/V2/Buttons/PillButton"
import GatsbyStoryblokImage from "@components/elements/V2/GatsbyStoryblokImage"
import Typography from "@components/elements/V2/Typography"
import LayoutSection from "@components/V2/LayoutComponents/LayoutSection"
import LayoutItem from "@components/V2/Layouts/LayoutItem"
import { Color } from "@constants/V2/color"
import { getAccentColor } from "@utils/V2/color"
import React, { useState } from "react"

type Props = {
  backgroundColor: Color
  headingText?: string
  headingBodyText?: string
  content: {
    tabLabel: string
    description: string
    ctaText?: string
    ctaLink?: string | YocoCom.YocoLink
    image: string
    imageAlt: string
    tags?: {
      ctaText: string
      ctaLink: string
    }[]
  }[]
}

const AccordionSlider = ({
  backgroundColor,
  headingText,
  headingBodyText,
  content,
  ...props
}: Props) => {
  const [activeIndex, setActiveIndex] = useState(0)

  return (
    <LayoutItem
      className="overflow-x-hidden"
      as={LayoutSection}
      primaryBackgroundColor={backgroundColor}
      {...props}
    >
      <Typography
        text={headingText ?? ""}
        size="h4"
        font="grotesk"
        color={getAccentColor(backgroundColor)}
        weight="medium"
      />
      <Typography
        text={headingBodyText ?? ""}
        size="body-md"
        font="grotesk"
        color={getAccentColor(backgroundColor)}
        weight="book"
        className="mt-[16px] max-w-[701px]"
      />

      {/* Desktop */}

      <div className="hidden sm:block">
        <div className="mt-[41px] flex flex-row gap-[32px]">
          {content.map((item, index) => (
            <Typography
              key={`${item.tabLabel}-${index}`}
              text={item.tabLabel}
              size="body-lg"
              font="grotesk"
              color={getAccentColor(backgroundColor)}
              weight="medium"
              className={classNames(
                "cursor-pointer transition-opacity duration-300",
                {
                  "opacity-100": activeIndex === index,
                  "opacity-50": activeIndex !== index,
                }
              )}
              onClick={() => setActiveIndex(index)}
            />
          ))}
        </div>

        <div className="mt-[17px] flex flex-row gap-grid-sm md:gap-grid-md lg:gap-grid-lg xl:gap-grid-xl">
          {content.map((item, index) => {
            const isActive = activeIndex === index
            const slideClassName = classNames(
              "group relative overflow-hidden rounded-[20px] text-left transition-all duration-300",
              item.ctaLink ? "cursor-pointer" : "cursor-default",
              {
                "w-[min(600px,_50vw)]": isActive,
                "w-[min(800px,_70vw)]": isActive && content.length === 3,
                "w-[min(179px,_10vw)] md:w-[min(600px,calc((100%-600px-72px)/3))] lg:w-[min(600px,calc((100%-600px-96px)/3))] xl:w-[min(600px,calc((100%-600px-120px)/3))]":
                  !isActive,
              }
            )

            const slideContent = (
              <>
                <div
                  className={classNames(
                    "absolute bottom-0 z-10 flex w-[min(600px,_50vw)] flex-col p-40 opacity-0 transition-opacity duration-300",
                    {
                      "w-[min(800px,_70vw)]": content.length === 3,
                      "opacity-100": isActive,
                    }
                  )}
                >
                  <Typography
                    text={item.description}
                    size="lead-lg"
                    font="grotesk"
                    color={Color.White}
                    weight="medium"
                    className="w-full"
                  />

                  {item.tags && item.tags.length > 0 && (
                    <div className="mt-24 flex gap-12">
                      {item.tags.map((tag) => (
                        <a
                          key={tag.ctaText}
                          href={tag.ctaLink}
                          className="rounded-lg border-[1.5px] border-white/30 bg-white/10 px-16 py-10 transition-colors duration-200 hover:border-white/50 hover:bg-white/20"
                        >
                          <Typography
                            text={tag.ctaText}
                            size="body-sm"
                            font="grotesk"
                            color={Color.White}
                            weight="medium"
                          />
                        </a>
                      ))}
                    </div>
                  )}
                  {item.ctaText && item.ctaLink && (
                    <PillButton
                      text={item.ctaText}
                      style="solid"
                      size="large"
                      color={Color.Blue}
                      linkUrl={item.ctaLink}
                      className="mt-[22px]"
                    />
                  )}
                </div>

                <div className="relative">
                  <div
                    className="absolute inset-0 z-[1] rounded-[20px]"
                    style={{
                      background:
                        "linear-gradient(191.96deg, rgba(34, 34, 34, 0) 51.89%, rgba(34, 34, 34, 0.9) 91.26%)",
                    }}
                  />

                  <GatsbyStoryblokImage
                    image={item.image}
                    alt={item.imageAlt}
                    height={570}
                    width={content.length === 3 ? 800 : 600}
                    className="h-auto w-full object-cover transition-transform duration-500 group-hover:scale-110"
                  />
                </div>
              </>
            )

            if (item.ctaLink) {
              return (
                <Link
                  key={`${item.tabLabel}-content-${index}`}
                  link={item.ctaLink}
                  className={slideClassName}
                  onMouseEnter={() => setActiveIndex(index)}
                  onFocus={() => setActiveIndex(index)}
                  aria-label={`View ${item.tabLabel} content`}
                >
                  {slideContent}
                </Link>
              )
            }

            return (
              <button
                key={`${item.tabLabel}-content-${index}`}
                className={slideClassName}
                onMouseOver={() => setActiveIndex(index)}
                onFocus={() => setActiveIndex(index)}
                onKeyDown={(e) => {
                  if (e.key === "Enter" || e.key === " ") {
                    e.preventDefault()
                    setActiveIndex(index)
                  }
                }}
                onClick={() => setActiveIndex(index)}
                aria-label={`View ${item.tabLabel} content`}
              >
                {slideContent}
              </button>
            )
          })}
        </div>
      </div>

      {/* Mobile */}

      <div className="mt-40 block min-[350px]:-mr-[30px] sm:hidden">
        <Swiper className="w-full" slidesPerView="auto">
          {content.map((item, index) => (
            <SwiperSlide
              key={`${item.tabLabel}-slide-${index}`}
              className={classNames(
                "mr-[16px] grid h-auto w-[305px] last:mr-[calc(100vw-5.33vw-305px)] last:sm:mr-[calc(100vw-4.17vw-580px)] md:w-[390px] last:md:mr-[calc(100vw-4.17vw-810px)] lg:w-[495px] last:lg:mr-[calc(100vw-7.22vw-1026px)] last:xl:mr-[calc(100vw-9.76vw-1024px)] last:4k:mr-0"
              )}
            >
              {item.ctaLink ? (
                <Link
                  link={item.ctaLink}
                  className="relative block h-[406px] w-[305px]"
                  aria-label={item.tabLabel}
                >
                  <div className="absolute bottom-0 z-20 p-20 pb-40">
                    <Typography
                      text={item.description}
                      size="lead-md"
                      font="grotesk"
                      color={Color.White}
                      weight="medium"
                    />
                    {item.ctaText && (
                      <PillButton
                        text={item.ctaText}
                        style="solid"
                        size="large"
                        color={Color.Blue}
                        linkUrl={item.ctaLink}
                        className="mt-20"
                      />
                    )}
                  </div>

                  <div className="flex-shrink-0 overflow-hidden rounded-[20px]">
                    <div
                      className="absolute inset-0 z-[1] rounded-[20px]"
                      style={{
                        background:
                          "linear-gradient(191.96deg, rgba(34, 34, 34, 0) 51.89%, rgba(34, 34, 34, 0.9) 91.26%)",
                      }}
                    />

                    <GatsbyStoryblokImage
                      image={item.image}
                      alt={item.imageAlt}
                      height={406}
                      width={305}
                      rounded={true}
                      className="h-auto w-full object-cover"
                    />
                  </div>
                </Link>
              ) : (
                <div className="relative h-[406px] w-[305px]">
                  <div className="absolute bottom-0 z-20 p-20 pb-40">
                    <Typography
                      text={item.description}
                      size="lead-md"
                      font="grotesk"
                      color={Color.White}
                      weight="medium"
                    />
                    {item.ctaText && item.ctaLink && (
                      <PillButton
                        text={item.ctaText}
                        style="solid"
                        size="large"
                        color={Color.Blue}
                        linkUrl={item.ctaLink}
                        className="mt-20"
                      />
                    )}
                  </div>

                  <div className="flex-shrink-0 overflow-hidden rounded-[20px]">
                    <div
                      className="absolute inset-0 z-[1] rounded-[20px]"
                      style={{
                        background:
                          "linear-gradient(191.96deg, rgba(34, 34, 34, 0) 51.89%, rgba(34, 34, 34, 0.9) 91.26%)",
                      }}
                    />

                    <GatsbyStoryblokImage
                      image={item.image}
                      alt={item.imageAlt}
                      height={406}
                      width={305}
                      rounded={true}
                      className="h-auto w-full object-cover"
                    />
                  </div>
                </div>
              )}
            </SwiperSlide>
          ))}
        </Swiper>
      </div>
    </LayoutItem>
  )
}

export default AccordionSlider
